import { observer } from "mobx-react";
import { PortfolioType, SelectedPortfolioOption, usePortfolio } from "@/portfolio/portfolioDomain";
import { useDispatch, useRootStore, useTranslation } from "@/stores/RootStoreContext";
import { Fragment, useEffect, useState } from "react";
import {
  filterDecisions,
  FollowUpStatusFilter,
  followUpStatusFilters,
  getEmptyStateLabel,
  getFollowUpEventLogPopover,
  getFollowUpInfoLabel,
  getFollowUpStatusFilterLabel,
  getFollowUpStatusLabel,
  getFollowUpTypeLabel,
  mapToFollowUpTab,
  mapToFollowUpUrl,
  openProjectFollowUp,
  PortfolioDecision,
  PortfolioDecisionsStore,
} from "./followUpDomain";
import { useAppBar } from "@/appBar/useAppBar";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { AppBar } from "@/components-v2/AppBar";
import { isFailure, isOk, isPending } from "@/common/common";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Box, Card, Container, Grid, GridItem, VBox } from "@/components-v2/Box";
import { Icon } from "@/components-v2/Icon";
import { IconButton, LinkButton } from "@/components-v2/Button";
import { FollowUpLeftPanel } from "./followUpComponents";
import { Tabs } from "@/components-v2/Tabs";
import { RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { MTextInput } from "@/components-v2/TextInput";
import { MSelect } from "@/components-v2/Select";
import { FollowUp, FollowUpStatus, getDecisionName } from "@/dbot/common/commonDomain";
import { classNames } from "@/utils/classNames";
import { when } from "@/utils/functions";
import { InfoButton } from "@/components-v2/InfoButton";
import { Dialog } from "@/components-v2/Dialog";
import { Markdown } from "@/text/Markdown";
import { Popover } from "@/components-v2/Popover";
import { EventLogDescription } from "@/common/eventLogs";

export const FollowUpDecisionsPage = observer(function PortfolioMattersPage({
  portfolioType,
  portfolioId,
  tab,
  contextCompany,
}: {
  portfolioType: PortfolioType;
  portfolioId?: string;
  tab?: string;
  contextCompany?: string;
}) {
  const { userStore, messageStore, http } = useRootStore();
  const { t } = useTranslation();
  const { portfolio } = usePortfolio({
    activeHeader: "follow-up",
    portfolioId,
    portfolioType,
  });

  const [store] = useState(new PortfolioDecisionsStore(http, userStore, messageStore, t));
  useEffect(() => {
    store.fetchDecisions(portfolio, contextCompany);
  }, [store, portfolio, contextCompany]);
  const isCompanyContextPage = contextCompany !== undefined;
  const appBar = useAppBar({
    portfolioHeader: "follow-up",
    isPortfolioPage: !isCompanyContextPage,
    selectedPortfolioOption: portfolio,
  });

  const followUpTab = mapToFollowUpTab(tab);
  return (
    <FullScreenLayout
      alertStore={messageStore}
      appBar={<AppBar data={appBar} disableDropShadow />}
      centerPanelContainerProps={{ htmlProps: { style: { flex: "1 1 auto" } } }}
      leftPanelContainerProps={{ htmlProps: { style: { flex: "0 0 300px" } } }}
      leftPanel={
        <FollowUpLeftPanel
          portfolio={portfolio}
          selectedTab={"matters"}
          showAdminLinks={store.showAdminLinks}
          contextCompany={contextCompany}
        />
      }
      centerPanel={
        isOk(store.decisionsResponse) && store.companyFilter ? (
          <>
            <FollowUpHeader
              portfolio={portfolio}
              activeTab={followUpTab}
              numberOfDecisionsByStatus={store.numberOfDecisionsByStatus}
              contextCompany={contextCompany}
            />
            <FollowUpDecisionsView
              followUpTab={followUpTab}
              decisions={store.portfolioDecisions}
              textFilter={store.textFilter}
              companyFilter={store.companyFilter}
              contextCompany={contextCompany}
              dismissDecision={(decision) => store.showDismissDecisionDialog(decision)}
            />
            {store.showDismissDecisionDialogForDecision && (
              <DismissDecisionDialog
                store={store}
                portfolio={portfolio}
                decision={store.showDismissDecisionDialogForDecision}
                contextCompany={contextCompany}
              />
            )}
          </>
        ) : isPending(store.decisionsResponse) ? (
          <LoadingIndicator fullscreen />
        ) : isFailure(store.decisionsResponse) ? (
          <VBox alignItems={"center"} justifyContent={"center"} fullheight>
            <Icon name={"warning-circle"} size={"jumbo"} color={"warning"} />
            <Box>{t("error.fetch.data")}</Box>
            <LinkButton
              label={{ key: "try-again" }}
              onClick={() => store.fetchDecisions(portfolio, contextCompany)}
            />
          </VBox>
        ) : undefined
      }
      disableAppBarShadow
    />
  );
});

const FollowUpHeader = observer(function FollowUpHeader({
  activeTab,
  portfolio,
  numberOfDecisionsByStatus,
  contextCompany,
}: {
  activeTab: FollowUpStatusFilter;
  portfolio: SelectedPortfolioOption;
  numberOfDecisionsByStatus?: Map<FollowUpStatusFilter, number>;
  contextCompany?: string;
}) {
  const { t } = useTranslation();
  const history = useRootStore().history;
  const tabs = followUpStatusFilters.map((filter) => ({
    id: filter,
    label: {
      key: getFollowUpStatusFilterLabel(filter),
      params: { count: numberOfDecisionsByStatus?.get(filter) },
    },
    onSelect: () => history.push(mapToFollowUpUrl(portfolio, filter, contextCompany)),
    isActive: activeTab === filter,
  }));

  return (
    <Box bg>
      <VBox maxWidth="xl" container marginTop="lg">
        <h4>{t("portfolio.matters")}</h4>
        <Tabs tabs={tabs} />
      </VBox>
    </Box>
  );
});

const FollowUpDecisionsView = observer(function PortfolioDecisionsView({
  decisions,
  textFilter,
  companyFilter,
  followUpTab,
  contextCompany,
  dismissDecision,
}: {
  decisions: PortfolioDecision[];
  textFilter: StringFieldModel;
  companyFilter: RadioFieldModel<string | null>;
  followUpTab: FollowUpStatusFilter;
  contextCompany?: string;
  dismissDecision: (decision: PortfolioDecision) => void;
}) {
  const dispatch = useDispatch();
  const { history } = useRootStore();
  const { t } = useTranslation();
  const filteredDecisions = filterDecisions(
    decisions,
    followUpTab,
    textFilter.inputValue,
    companyFilter.value,
    t,
  );
  const notInContextCompany = contextCompany === undefined;
  return decisions.length === 0 && <PortfolioDecisionsEmptyState followUpTab={followUpTab} /> ? (
    <PortfolioDecisionsEmptyState followUpTab={followUpTab} />
  ) : (
    <Container padding maxWidth="xl">
      <Box>
        <Box>
          <MTextInput model={textFilter} placeholder={t("search")} type={"search"} />
        </Box>
        <Box>{notInContextCompany && <MSelect model={companyFilter} />}</Box>
      </Box>
      {filteredDecisions.map((decision) => (
        <Card key={`${decision.orgNo}/${decision.decisionId}`} padding={"lg"} gap={"md"}>
          <Box justifyContent={"space-between"}>
            <LinkButton
              weight={"normal"}
              label={
                decision.projectName
                  ? `${decision.companyName} / ${decision.projectName}`
                  : decision.companyName
              }
              onClick={() => dispatch(openProjectFollowUp(decision, history.location.pathname))}
            />
            <IconButton
              icon={"x"}
              tooltip={{ key: "portfolio.matters.remove.tooltip" }}
              onClick={() => dismissDecision(decision)}
            />
          </Box>
          <VBox>
            <h5>{t(getDecisionName(decision.decisionType))}</h5>
            {decision.followUps.map((followUp) => (
              <FollowUpButton
                key={
                  followUp.data.type === "attachment"
                    ? followUp.data.attachmentType
                    : followUp.data.type === "sendToBR" && followUp.data.isRegistration
                      ? "sendRegistrationToBR"
                      : followUp.data.type
                }
                filteredDecision={decision}
                decision={decisions.find((d) => d.decisionId === decision.decisionId)}
                followUp={followUp}
              />
            ))}
          </VBox>
        </Card>
      ))}
    </Container>
  );
});

const PortfolioDecisionsEmptyState = observer(function PortfolioDecisionsEmptyState({
  followUpTab,
}: {
  followUpTab: FollowUpStatusFilter;
}) {
  const { t } = useTranslation();
  return (
    <Container marginY={"lg"} maxWidth="xl">
      <VBox marginY={"lg"} justifyContent={"center"} alignItems={"center"}>
        <Icon name={"list-magnifying-glass"} size={"mega"} weight={"thin"} />
        {t(getEmptyStateLabel(followUpTab))}
      </VBox>
    </Container>
  );
});

const FollowUpButton = observer(function FollowUpButton({
  filteredDecision,
  decision,
  followUp,
}: {
  filteredDecision: PortfolioDecision;
  decision?: PortfolioDecision;
  followUp: FollowUp;
}) {
  const dispatch = useDispatch();
  const history = useRootStore().history;
  const { t } = useTranslation();
  return (
    <Box
      gap={false}
      paddingY={"md"}
      paddingRight
      grow
      className={classNames("cursor-pointer", "hover:bg-alt2")}
      rounded
      htmlProps={{
        onClick: () => dispatch(openProjectFollowUp(filteredDecision, history.location.pathname)),
      }}
    >
      <Box paddingX alignItems={"center"}>
        {followUp.status === FollowUpStatus.READY && followUp.isActive && (
          <Icon name={"arrow-right"} color={"info"} />
        )}
        {followUp.status === FollowUpStatus.READY && !followUp.isActive && <Icon name={"clock"} />}
        {followUp.status === FollowUpStatus.IN_PROGRESS && <Icon name={"arrows-clockwise"} />}
        {followUp.status === "COMPLETED" && (
          <Icon name={"check"} color={"success"} weight={"bold"} />
        )}
      </Box>
      {when(getFollowUpTypeLabel(followUp.data, decision?.followUps ?? []), (message) => (
        <Box grow alignItems={"center"}>
          {t(message)}
        </Box>
      ))}
      {when(getFollowUpStatusLabel(followUp, filteredDecision.eventLogs), (message) => (
        <Box fg={"subtle"} alignItems={"center"}>
          {t(message)}
        </Box>
      ))}
      {when(getFollowUpInfoLabel(followUp, filteredDecision, decision?.followUps), (message) => (
        <Box alignItems={"center"} paddingLeft={"sm"}>
          <InfoButton infoText={t(message)} />
        </Box>
      ))}
      {when(
        getFollowUpEventLogPopover(followUp, filteredDecision.eventLogs),
        (eventLogDescriptions) => (
          <DecisionBREventLogPopover eventLogDescriptions={eventLogDescriptions} />
        ),
      )}
    </Box>
  );
});

const DismissDecisionDialog = observer(
  ({
    store,
    portfolio,
    decision,
    contextCompany,
  }: {
    store: PortfolioDecisionsStore;
    portfolio: SelectedPortfolioOption;
    decision: PortfolioDecision;
    contextCompany?: string;
  }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={{ key: "portfolio.matters.remove.confirm.title" }}
        onSubmit={() =>
          store.dismissDecision(portfolio, decision, contextCompany, () => (
            <DismissDecisionSuccessMessage
              companyName={decision.companyName}
              undoDismissal={() => store.undoDismissDecision(portfolio, decision, contextCompany)}
            />
          ))
        }
        onCancel={store.closeDismissDecisionDialog}
        submitLabel={{ key: "continue" }}
      >
        <Markdown
          value={t("portfolio.matters.remove.confirm.message", {
            companyName: decision.companyName,
          })}
          enableLinks
        />
      </Dialog>
    );
  },
);

function DismissDecisionSuccessMessage({
  companyName,
  undoDismissal,
}: {
  companyName: string;
  undoDismissal: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Box alignItems={"start"}>
      {t("portfolio.matters.remove.success", { companyName })}{" "}
      <LinkButton
        label={t("portfolio.matters.remove.success.undo")}
        onClick={undoDismissal}
        underline
        weight={"semibold"}
      />
    </Box>
  );
}

function DecisionBREventLogPopover({
  eventLogDescriptions,
}: {
  eventLogDescriptions: EventLogDescription[];
}) {
  const { t } = useTranslation();

  return (
    <Box alignItems={"center"} paddingLeft={"sm"}>
      <Popover
        button={
          <IconButton
            icon={"clock-counter-clockwise"}
            tooltip={t("portfolio.matters.altinn-events.show-history")}
            onClick={(event) => event.stopPropagation()}
          />
        }
        content={
          <VBox>
            <strong>{t("portfolio.matters.altinn-events.title")}</strong>
            <Grid cols={3}>
              {eventLogDescriptions.map((log) => (
                <Fragment key={log.timestamp}>
                  <GridItem colSpan={1} className={"subtle"}>
                    {t({
                      type: "date",
                      value: log.timestamp,
                    })}
                  </GridItem>
                  <GridItem colSpan={2}>{t(log.description)}</GridItem>
                </Fragment>
              ))}
            </Grid>
          </VBox>
        }
      />
    </Box>
  );
}
